import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { User, Mail, Phone, FileText, Shield, Key, Save, Calendar, Bell, BellOff, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { member, isAdmin, getToken, fetchMe } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({ nickname: "", email: "", bio: "", phone: "" });
  const [phonePrivate, setPhonePrivate] = useState(false);
  const [showCodeChange, setShowCodeChange] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [changingCode, setChangingCode] = useState(false);
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);
  const [togglingNewsletter, setTogglingNewsletter] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/member-codes/me`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        setProfile(res.data);
        setForm({
          nickname: res.data.nickname || "",
          email: res.data.email || "",
          bio: res.data.bio || "",
          phone: res.data.phone || "",
        });
        setPhonePrivate(res.data.phone_private || false);
      } catch {
        toast.error("Kunne ikke hente profil");
      } finally {
        setLoading(false);
      }
    };
    const fetchNewsletterStatus = async () => {
      try {
        const res = await axios.get(`${API}/newsletter/my-status`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        setNewsletterSubscribed(res.data.subscribed);
      } catch {
        // Silently fail
      }
    };
    fetchProfile();
    fetchNewsletterStatus();
  }, [getToken]);

  const handleToggleNewsletter = async () => {
    setTogglingNewsletter(true);
    try {
      const res = await axios.post(`${API}/newsletter/toggle-my-subscription`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setNewsletterSubscribed(res.data.subscribed);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke ændre nyhedsbrev-status");
    } finally {
      setTogglingNewsletter(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/member-codes/profile`, { ...form, phone_private: phonePrivate }, {
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
      });
      setProfile(res.data);
      setPhonePrivate(res.data.phone_private || false);
      await fetchMe();
      toast.success("Profil opdateret");
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke opdatere profil");
    } finally {
      setSaving(false);
    }
  };

  const handleCodeChange = async () => {
    if (newCode.length < 4) { toast.error("Koden skal være mindst 4 tegn"); return; }
    setChangingCode(true);
    try {
      await axios.put(`${API}/member-codes/my-code`, { new_code: newCode }, {
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
      });
      toast.success("Din kode er opdateret");
      setShowCodeChange(false);
      setNewCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke ændre kode");
    } finally {
      setChangingCode(false);
    }
  };

  const roleLabels = { admin: "Gruppe 1 — Administrator", editor: "Gruppe 2 — Redaktør", member: "Gruppe 3 — Medlem" };
  const roleColors = { admin: "text-purple-400 bg-purple-500/10 border-purple-500/30", editor: "text-amber-400 bg-amber-500/10 border-amber-500/30", member: "text-[#00d4ff] bg-[#00d4ff]/10 border-[#00d4ff]/30" };

  if (loading) return (
    <div className="min-h-screen pt-24 flex justify-center">
      <div className="w-8 h-8 border-2 border-[#00d4ff]/30 border-t-[#00d4ff] rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="min-h-screen pt-20 pb-12 px-4" data-testid="profile-page">
      <div className="max-w-xl mx-auto">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-white transition-all mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Tilbage
        </button>
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-gray-700 flex items-center justify-center" style={{ boxShadow: "0 0 20px rgba(0,0,0,0.6)" }}>
            <User size={36} className="text-gray-500" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-['Oswald'] font-bold text-white tracking-wide" data-testid="profile-name">
            {profile?.nickname || profile?.member_name}
          </h1>
          {profile?.nickname && (
            <p className="text-gray-400 text-sm italic mt-1">Civilt kendt som: {profile?.member_name}</p>
          )}
          {(isAdmin || profile?.role === 'editor') && <div className="mt-3">
            <span className={`inline-flex items-center gap-2 px-4 py-1.5 text-xs font-['Oswald'] uppercase tracking-wider rounded-full border ${roleColors[profile?.role] || roleColors.member}`} data-testid="profile-role-badge">
              <Shield size={12} />
              {roleLabels[profile?.role] || "Medlem"}
            </span>
          </div>}
          {profile?.created_at && (
            <p className="text-gray-600 text-xs mt-2 flex items-center justify-center gap-1">
              <Calendar size={11} />
              Medlem siden {new Date(profile.created_at).toLocaleDateString("da-DK", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          )}
        </div>

        {isAdmin && <div className="mc-card p-4 mb-6" style={{ boxShadow: "0 0 20px rgba(0,0,0,0.6)" }}>
          <p className="text-gray-500 text-xs text-center">
            Din gruppe bestemmes af en administrator og kan ikke ændres af dig selv.
          </p>
        </div>}

        {/* Profile form */}
        <div className="mc-card p-6 space-y-5" style={{ boxShadow: "0 0 25px rgba(0,0,0,0.6)" }}>
          <h2 className="text-lg font-['Oswald'] text-white tracking-wide border-b border-gray-800 pb-3">
            REDIGER PROFIL
          </h2>

          <div>
            <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5">
              <User size={12} /> Klubnavn
            </label>
            <input
              data-testid="profile-nickname-input"
              className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none transition-colors"
              value={form.nickname}
              onChange={(e) => setForm(p => ({ ...p, nickname: e.target.value }))}
              placeholder="F.eks. DD, Tansen, Gansen..."
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5">
              <Mail size={12} /> Email
            </label>
            <input
              data-testid="profile-email-input"
              type="email"
              className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none transition-colors"
              value={form.email}
              onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
              placeholder="din@email.dk"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5">
              <Phone size={12} /> Telefon
            </label>
            <input
              data-testid="profile-phone-input"
              type="tel"
              className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none transition-colors"
              value={form.phone}
              onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
              placeholder="+45 12 34 56 78"
            />
            <button
              type="button"
              onClick={() => setPhonePrivate(prev => !prev)}
              className="flex items-center gap-2 mt-2 group"
              data-testid="phone-private-toggle"
            >
              <div className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${phonePrivate ? 'bg-green-500' : 'bg-gray-700'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${phonePrivate ? 'translate-x-[18px]' : 'translate-x-0.5'}`}></div>
              </div>
              <span className={`text-xs transition-colors ${phonePrivate ? 'text-green-400' : 'text-gray-500'}`}>
                {phonePrivate ? "Kan kun ses internt (klubmedlemmer)" : "Synligt for alle"}
              </span>
            </button>
          </div>

          <div>
            <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5">
              <FileText size={12} /> Om mig
            </label>
            <textarea
              data-testid="profile-bio-input"
              className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none transition-colors resize-none"
              rows={4}
              maxLength={500}
              value={form.bio}
              onChange={(e) => setForm(p => ({ ...p, bio: e.target.value }))}
              placeholder="Fortæl lidt om dig selv..."
            />
            <p className="text-right text-xs text-gray-600 mt-1">{form.bio.length}/500</p>
          </div>

          <button
            onClick={handleSave}
            disabled={saving || saved}
            className="w-full chrome-btn py-3 text-sm flex items-center justify-center gap-2 font-['Oswald'] uppercase tracking-wider"
            data-testid="profile-save-btn"
          >
            {saving ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Gemmer...</> : saved ? <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Gemt!</> : <><Save size={16} />Gem Profil</>}
          </button>
        </div>

        {/* Change code section */}
        <div className="mc-card p-6 mt-6" style={{ boxShadow: "0 0 25px rgba(0,0,0,0.6)" }}>
          <h2 className="text-lg font-['Oswald'] text-white tracking-wide border-b border-gray-800 pb-3 mb-4">
            SIKKERHED
          </h2>
          {!showCodeChange ? (
            <button
              onClick={() => setShowCodeChange(true)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#00d4ff] transition-colors font-['Oswald'] uppercase tracking-wider"
              data-testid="profile-change-code-btn"
            >
              <Key size={14} /> Skift personlig kode
            </button>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5">Ny kode (mindst 4 tegn)</label>
                <input
                  data-testid="profile-new-code-input"
                  className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none transition-colors font-mono tracking-widest"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  placeholder="NY KODE"
                  maxLength={12}
                />
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setShowCodeChange(false); setNewCode(""); }} className="flex-1 px-4 py-2.5 text-sm text-gray-400 border border-gray-800 hover:text-white rounded transition-all font-['Oswald'] uppercase tracking-wider">
                  Annuller
                </button>
                <button
                  onClick={handleCodeChange}
                  disabled={changingCode}
                  className="flex-1 chrome-btn px-4 py-2.5 text-sm flex items-center justify-center gap-2"
                  data-testid="profile-save-code-btn"
                >
                  {changingCode ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Gemmer...</> : <><Key size={14} />Gem Kode</>}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Newsletter section */}
        <div className="mc-card p-6 mt-6" style={{ boxShadow: "0 0 25px rgba(0,0,0,0.6)" }}>
          <h2 className="text-lg font-['Oswald'] text-white tracking-wide border-b border-gray-800 pb-3 mb-4">
            NYHEDSBREV
          </h2>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              {newsletterSubscribed ? <Bell size={18} className="text-amber-400" /> : <BellOff size={18} className="text-gray-500" />}
              <div>
                <p className="text-sm text-gray-300">
                  {newsletterSubscribed ? "Du modtager nyhedsbreve om events" : "Du modtager ikke nyhedsbreve"}
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  {form.email ? `Sendes til ${form.email}` : "Tilføj email ovenfor for at tilmelde"}
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleNewsletter}
              disabled={togglingNewsletter || !form.email}
              className={`px-4 py-2 text-xs font-['Oswald'] uppercase tracking-wider rounded transition-all ${
                newsletterSubscribed
                  ? "border border-red-500/30 text-red-400 hover:bg-red-500/10"
                  : "border border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
              } disabled:opacity-40`}
              data-testid="newsletter-toggle-btn"
            >
              {togglingNewsletter
                ? <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin inline-block" />
                : newsletterSubscribed ? "Afmeld" : "Tilmeld"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
